import { lazy, type ComponentType } from "react";

// Every deploy gives the bundled chunks new hashed filenames and deletes the
// old ones. A browser that still has an OLD page in memory will 404 when it
// lazy-loads a route chunk that no longer exists — surfacing as
// "Failed to fetch dynamically imported module" / "importing a module script
// failed". This wrapper turns that into a single full reload, which pulls the
// fresh index.html + current chunks and the navigation then succeeds.
//
// Guarded by a timestamp in sessionStorage so a genuinely broken import (not a
// stale-deploy mismatch) can't reload-loop — after one reload it rethrows and
// lets the error boundary show.
const RELOAD_KEY = "lastChunkReload";
const RELOAD_ATTEMPTS_KEY = "chunk_reload_attempts";
const RELOAD_WINDOW_MS = 30_000;
const MAX_RELOAD_ATTEMPTS = 2;

function cacheBustedHref(): string {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("r", String(Date.now()));
    return url.toString();
  } catch {
    return `${window.location.pathname}?r=${Date.now()}`;
  }
}

function reloadFreshBuild() {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    if ("caches" in window) {
      caches.keys().then((names) => {
        for (const name of names) caches.delete(name);
      }).catch(() => {});
    }
  } catch {}
  window.location.href = cacheBustedHref();
}

export function reloadOnceForStaleChunk(): boolean {
  try {
    const now = Date.now();
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    const stored = JSON.parse(localStorage.getItem(RELOAD_ATTEMPTS_KEY) || "null") as { count?: number; startedAt?: number } | null;
    const windowExpired = !stored?.startedAt || now - stored.startedAt > RELOAD_WINDOW_MS;
    const next = windowExpired ? { count: 1, startedAt: now } : { count: (stored.count || 0) + 1, startedAt: stored.startedAt };

    if (Date.now() - last > 2_000 && next.count <= MAX_RELOAD_ATTEMPTS) {
      localStorage.setItem(RELOAD_ATTEMPTS_KEY, JSON.stringify(next));
      reloadFreshBuild();
      return true;
    }
  } catch {
    reloadFreshBuild();
    return true;
  }
  return false;
}

export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  // Non-critical widgets (chat bubble, analytics) pass silentFallback so that a
  // chunk that STILL won't resolve after the one-time reload degrades to
  // rendering nothing instead of throwing up to the page error boundary. A
  // failing chat bubble must never crash the storefront.
  opts?: { silentFallback?: ComponentType<any> },
) {
  const handleFailure = (err: any) => {
    if (reloadOnceForStaleChunk()) {
      // Hang until the reload takes over so React doesn't flash an error.
      return new Promise<{ default: T }>(() => {});
    }
    if (opts?.silentFallback) {
      return { default: opts.silentFallback as unknown as T };
    }
    throw err;
  };

  return lazy(() =>
    factory()
      .then((mod) => {
        // 2026-07-09 — a stale chunk hash doesn't always REJECT the import.
        // On 2026-07-08's back-to-back deploys, several real visitors hit
        // "Cannot read properties of undefined (reading 'default')" /
        // "e._result.default" — the import() promise RESOLVED, just with no
        // usable module (the server's SPA fallback likely served index.html
        // in place of a 404'd chunk, or a corrupted partial download parsed
        // to an empty module). That silently slipped past the .catch()
        // below and crashed straight through to the error boundary — no
        // page recovered, no reload, customer just stuck on a broken page.
        // Treat a resolved-but-empty module the same as an outright
        // rejection so it gets the same one-time-reload recovery.
        if (!mod || mod.default == null) {
          return handleFailure(new Error("Lazy chunk resolved without a valid default export"));
        }
        return mod;
      })
      .catch(handleFailure),
  );
}
